/**
 * Shared framer-motion animation utilities for CleanBid
 *
 * Design spec:
 *   - opacity 0→1, y 20px→0, duration 0.5s, easing [0.16, 1, 0.3, 1]
 *   - stagger: 0.06–0.1s between children
 *   - trigger: enter viewport once (don't re-trigger on scroll up)
 *   - all animations respect prefers-reduced-motion
 */
import { useRef, useState, useEffect } from "react";
import {
  motion,
  useInView,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useMotionValueEvent,
} from "framer-motion";

const prefersReduced =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const EASING = [0.16, 1, 0.3, 1];
export const DURATION = 0.5;

/**
 * FadeUp — scroll-triggered fade + slide-up reveal.
 * Drop-in replacement for the CSS-based FadeUp across all pages.
 * `delay` is in milliseconds (backwards-compatible with existing calls).
 * `y` controls the starting offset (default 20px, use 40 for hero visuals).
 */
export function FadeUp({ children, delay = 0, className = "", y = 20 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.08 });

  if (prefersReduced) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: DURATION, ease: EASING, delay: delay / 1000 }}
    >
      {children}
    </motion.div>
  );
}

/**
 * AccordionContent — AnimatePresence-based height animation for FAQ accordions.
 * Replaces the CSS max-height: 0 / max-height: 24rem transition.
 */
export function AccordionContent({ children, open }) {
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="accordion-body"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.32, ease: EASING }}
          className="overflow-hidden"
        >
          <div className="pb-5">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * CardHover — smooth y:-3px lift on hover.
 * Replaces the CSS hover:-translate-y-1 on card containers.
 * Add this as a wrapping div, or apply className for layout/structural classes.
 */
export function CardHover({ children, className = "" }) {
  if (prefersReduced) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

/**
 * StaggerTableBody — animates tbody rows in one-by-one as the table enters view.
 * Use in place of <tbody>; rows inside must use <motion.tr variants={tableRowVariant}>.
 */
const staggerBodyVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

export const tableRowVariant = {
  hidden: { opacity: 0, y: 6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: EASING },
  },
};

export function StaggerTableBody({ children, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.1 });

  if (prefersReduced) return <tbody className={className}>{children}</tbody>;

  return (
    <motion.tbody
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={staggerBodyVariants}
    >
      {children}
    </motion.tbody>
  );
}

/**
 * useSpringCount — returns a plain integer that springs to `target` on change.
 * Re-animates briefly (spring physics) whenever target changes.
 * Use for ROI Calculator outputs and stat counters.
 */
export function useSpringCount(target) {
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 160, damping: 30, restDelta: 0.5 });
  const [val, setVal] = useState(0);

  useMotionValueEvent(spring, "change", (v) => setVal(Math.round(v)));

  useEffect(() => {
    mv.set(target);
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  return val;
}

export { motion, AnimatePresence, useInView };
